"use client";

import { c as _c } from "react/compiler-runtime";
import { useId, useRef } from "react";
import type { ReactNode } from "react";
import Tooltip from "@/components/ui/tooltip";
import { BODY_SEGMENT_LABELS } from "@/lib/measurement-body";
import { getSeverityFillClass } from "@/lib/measurement-calculations";
import { cx } from "@/lib/tailwind-utils";
import type { BodySegment, SeverityLevel } from "@/types/measurement";
export interface BodySegmentVisual {
  severity: SeverityLevel | null;
  tooltip: ReactNode;
}
interface SegmentRect {
  x: number;
  y: number;
  width: number;
  height: number;
  rx: number;
}

/**
 * A pictogram, not a drawing of a body: a head and five boxes, every dimension
 * derived from the 96-unit torso width by the golden ratio. Head diameter 59
 * (96 / φ), torso 96 × 155 (1.615), arms 24 × 164 (φ⁴), legs 39 × 165 (φ³)
 * with the leg width an arm width × φ. Laid out the way InBody prints its
 * segmental figure: the patient's left side on the viewer's left.
 */
const SEGMENT_RECTS: Record<BodySegment, SegmentRect> = {
  TRUNK: {
    x: 72,
    y: 70,
    width: 96,
    height: 155,
    rx: 14
  },
  LEFT_ARM: {
    x: 42,
    y: 70,
    width: 24,
    height: 164,
    rx: 12
  },
  RIGHT_ARM: {
    x: 174,
    y: 70,
    width: 24,
    height: 164,
    rx: 12
  },
  LEFT_LEG: {
    x: 76,
    y: 230,
    width: 39,
    height: 165,
    rx: 16
  },
  RIGHT_LEG: {
    x: 125,
    y: 230,
    width: 39,
    height: 165,
    rx: 16
  }
};

// Left to right, top to bottom, so Tab moves through the limbs as they appear.
const FIGURE_ORDER: BodySegment[] = ["LEFT_ARM", "TRUNK", "RIGHT_ARM", "LEFT_LEG", "RIGHT_LEG"];
interface BodyFigureProps {
  segments: Record<BodySegment, BodySegmentVisual>;
  openSegment: BodySegment | null;
  hoveredSegment: BodySegment | null;
  onSegmentOpenChange: (segment: BodySegment, open: boolean) => void;
  onSegmentHover: (segment: BodySegment, hovered: boolean) => void;
}
export default function BodyFigure(t0) {
  const $ = _c(14);
  const {
    segments,
    openSegment,
    hoveredSegment,
    onSegmentOpenChange,
    onSegmentHover
  } = t0;
  const titleId = useId();
  const lastPointerTypeRef = useRef("mouse");
  let t1;
  if ($[0] !== titleId) {
    t1 = <title id={titleId}>Body diagram by segment</title>;
    $[0] = titleId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <circle cx={120} cy={35} r={30} className="fill-background-tertiary" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <g aria-hidden="true" className="fill-foreground-muted" fontSize={22}><text x={54} y={58} textAnchor="middle">L</text><text x={186} y={58} textAnchor="middle">R</text></g>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== hoveredSegment || $[5] !== onSegmentHover || $[6] !== onSegmentOpenChange || $[7] !== openSegment || $[8] !== segments) {
    t4 = FIGURE_ORDER.map(segment => {
      const visual = segments[segment];
      const rect = SEGMENT_RECTS[segment];
      const isOpen = openSegment === segment;
      const isActive = isOpen || hoveredSegment === segment;
      const segmentNode = <g role="button" tabIndex={0} aria-label={BODY_SEGMENT_LABELS[segment]} data-testid={`body-segment-${segment}`} className="cursor-pointer outline-none" onPointerDown={e => {
        lastPointerTypeRef.current = e.pointerType;
      }} onClick={e_0 => {
        if (lastPointerTypeRef.current === "mouse") {
          return;
        }
        onSegmentOpenChange(segment, !isOpen);
        e_0.preventDefault();
      }} onMouseEnter={() => onSegmentHover(segment, true)} onMouseLeave={() => onSegmentHover(segment, false)} onFocus={() => onSegmentHover(segment, true)} onBlur={() => onSegmentHover(segment, false)}><rect x={rect.x} y={rect.y} width={rect.width} height={rect.height} rx={rect.rx} strokeWidth={isActive ? 5 : 0} className={cx(getSeverityFillClass(visual?.severity ?? null), "transition-all", isActive && "stroke-foreground brightness-110")} /></g>;
      return <Tooltip key={segment} variant="light" content={visual?.tooltip} open={isOpen} onOpenChange={open => onSegmentOpenChange(segment, open)} triggerAsChild={true}>{segmentNode}</Tooltip>;
    });
    $[4] = hoveredSegment;
    $[5] = onSegmentHover;
    $[6] = onSegmentOpenChange;
    $[7] = openSegment;
    $[8] = segments;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== t1 || $[11] !== t4 || $[12] !== titleId) {
    t5 = <svg viewBox="32 -12 176 424" className="h-64 w-auto" role="group" aria-labelledby={titleId}>{t1}{t2}{t3}{t4}</svg>;
    $[10] = t1;
    $[11] = t4;
    $[12] = titleId;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
}